import {useInView} from '@aboutyou/ui-components';
import {useCallback, useRef, useState} from 'react';
import {useRectEffect} from 'react-viewport-utils';

const TABLET_MODE_MAX_WIDTH = 180;

export interface SearchBarOpenOrigin {
  left: number;
  width: number;
}

export const useSearchBarDisplayState = () => {
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const stickySearchIconRef = useRef<HTMLDivElement | null>(null);

  const [isSearchInTabletMode, setIsSearchInTabletMode] = useState(false);
  const [isSearchBarExpanded, setIsSearchBarExpanded] = useState(false);
  const [isSearchBarExtended, setIsSearchBarExtended] = useState(false);
  const [openOrigin, setOpenOrigin] = useState<SearchBarOpenOrigin | null>(
    null,
  );

  const {ref: searchWrapperRef, inView: isSearchWrapperInView} =
    useInView<HTMLDivElement>({
      initialInView: true,
      threshold: 0.1,
    });

  useRectEffect(
    rect => {
      if (!rect) {
        return;
      }

      const isTabletMode = rect.width < TABLET_MODE_MAX_WIDTH;
      setIsSearchInTabletMode(isTabletMode);

      if (!isTabletMode) {
        setIsSearchBarExpanded(false);
      }
    },
    searchWrapperRef,
    {priority: 'low'},
  );

  const expandSearchBar = useCallback(() => {
    if (isSearchInTabletMode) {
      setIsSearchBarExpanded(true);
    }
  }, [isSearchInTabletMode]);

  const collapseSearchBar = useCallback(() => {
    setIsSearchBarExpanded(false);
  }, []);

  // The icon is measured while it is still on screen: it leaves as soon as the
  // bar opens, and the bar needs its box to grow out of it.
  const handleStickySearchIconClick = useCallback(() => {
    const iconRect = stickySearchIconRef.current?.getBoundingClientRect();
    setOpenOrigin(
      iconRect ? {left: iconRect.left, width: iconRect.width} : null,
    );
    expandSearchBar();
    searchInputRef.current?.focus({preventScroll: true});
  }, [expandSearchBar]);

  // The bar reports `false` once more while it opens, as its effect cleans up
  // before it runs again, so only a real close drops the origin.
  const wasSearchBarExtended = useRef(false);
  const handleExtendedChange = useCallback((isExtended: boolean) => {
    setIsSearchBarExtended(isExtended);

    if (!isExtended && wasSearchBarExtended.current) {
      setOpenOrigin(null);
    }

    wasSearchBarExtended.current = isExtended;
  }, []);

  return {
    searchInputRef,
    stickySearchIconRef,
    openOrigin,
    isSearchInTabletMode,
    isSearchBarExpanded,
    expandSearchBar,
    collapseSearchBar,
    searchWrapperRef,
    handleStickySearchIconClick,
    setIsSearchBarExtended: handleExtendedChange,
    showStickyHeaderSearchIcon:
      isSearchWrapperInView === false &&
      !isSearchBarExpanded &&
      !isSearchBarExtended,
  };
};
